import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Rocket, 
  Upload, 
  Store, 
  CreditCard, 
  ShieldCheck, 
  User, 
  Headphones, 
  HelpCircle,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import '../styles/HelpCenter.css';

const HelpCenter = () => {
  const [activeCategory, setActiveCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const toggleCategory = (categoryId) => {
    setActiveCategory(activeCategory === categoryId ? null : categoryId);
  };

  const categories = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: <Rocket size={20} className="category-icon" />,
      articles: [
        {
          question: 'How do I create a Falphin account?',
          answer: 'Click "Sign Up" in the top right corner, enter your details, and verify your email. No credit card required to start.'
        },
        {
          question: 'What do I need to get started?',
          answer: 'You\'ll need your music files (WAV or high-quality MP3), artwork (JPG/PNG, min 3000x3000px), and basic track info (title, artist name, etc.).'
        },
        {
          question: 'Is there a free trial?',
          answer: 'We don\'t offer a free trial, but you can explore our platform and pricing with no commitment. Your first upload is covered by our one-time fee.'
        }
      ]
    },
    {
      id: 'uploading-music',
      title: 'Uploading Music',
      icon: <Upload size={20} className="category-icon" />,
      articles: [
        {
          question: 'What file formats do you accept?',
          answer: 'We accept WAV, FLAC, and high-quality MP3 (320kbps or higher). For best quality, we recommend WAV or FLAC.'
        },
        {
          question: 'How long does it take to distribute my music?',
          answer: 'After approval, distribution typically takes 1-3 business days to most platforms. Some stores may take up to 2 weeks.'
        },
        {
          question: 'Can I edit my release after submission?',
          answer: 'Yes, you can edit metadata before distribution starts. After distribution, changes may incur a small fee.'
        }
      ]
    },
    {
      id: 'stores-distribution',
      title: 'Stores & Distribution',
      icon: <Store size={20} className="category-icon" />,
      articles: [
        {
          question: 'Which platforms will my music be on?',
          answer: 'We distribute to all major platforms including Spotify, Apple Music, Amazon Music, Tidal, and more. See our full list on the Distribution page.'
        },
        {
          question: 'Do you distribute to YouTube Content ID?',
          answer: 'Yes, we can register your music with YouTube Content ID to help protect your work and earn from user-generated content.'
        },
        {
          question: 'Can I choose specific stores?',
          answer: 'Yes, you can select which stores and territories to distribute to during the upload process.'
        }
      ]
    },
    {
      id: 'payments-royalties',
      title: 'Payments & Royalties',
      icon: <CreditCard size={20} className="category-icon" />,
      articles: [
        {
          question: 'How do I get paid?',
          answer: 'We pay out via bank transfer, PayPal, or Payoneer once you reach the $50 minimum threshold.'
        },
        {
          question: 'What percentage does Falphin take?',
          answer: 'We take a small revenue share only when you earn. There are no hidden fees or annual charges.'
        },
        {
          question: 'How often are royalties paid?',
          answer: 'Royalties are paid monthly, approximately 45 days after the end of each month.'
        }
      ]
    },
    {
      id: 'content-rules',
      title: 'Content Rules',
      icon: <ShieldCheck size={20} className="category-icon" />,
      articles: [
        {
          question: 'What content is not allowed?',
          answer: 'We don\'t allow unauthorized samples, covers without proper licensing, or content that violates copyrights or our terms of service.'
        },
        {
          question: 'Can I upload covers or remixes?',
          answer: 'Yes, but you must obtain and provide proof of mechanical licenses for covers before distribution.'
        },
        {
          question: 'What happens if my content is rejected?',
          answer: 'We\'ll notify you with the reason for rejection and how to fix it. You can resubmit after making the necessary changes.'
        }
      ]
    },
    {
      id: 'account-management',
      title: 'Account Management',
      icon: <User size={20} className="category-icon" />,
      articles: [
        {
          question: 'How do I update my payment information?',
          answer: 'Go to Account Settings > Payment Info to update your payment method or banking details.'
        },
        {
          question: 'Can I change my artist name?',
          answer: 'Yes, but it may affect your existing releases. Contact support for assistance with artist name changes.'
        },
        {
          question: 'How do I delete my account?',
          answer: 'Contact our support team. Note that deleting your account won\'t remove your music from stores.'
        }
      ]
    },
    {
      id: 'support-contact',
      title: 'Support & Contact',
      icon: <HelpCircle size={20} className="category-icon" />,
      articles: [
        {
          question: 'How do I contact support?',
          answer: 'Use the contact form on our website or email support@falphin.com. We typically respond within 24-48 hours.'
        },
        {
          question: 'Do you offer phone support?',
          answer: 'We currently offer email and live chat support during business hours (9am-6pm EST).'
        },
        {
          question: 'Where can I find tutorials?',
          answer: 'Check our YouTube channel and blog for video tutorials and guides on using Falphin.'
        }
      ]
    }
  ];

  const filteredCategories = categories.map(category => ({
    ...category,
    articles: category.articles.filter(article => 
      article.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.answer.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.articles.length > 0);

  return (
    <div className="help-center">
      <div className="help-hero">
        <h1>How can we help you today?</h1>
        <div className="search-container">
          <input
            type="text"
            placeholder="Search help articles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <Headphones size={20} className="search-icon" />
        </div>
      </div>

      <div className="help-categories">
        {filteredCategories.map((category) => (
          <div key={category.id} className="category-card">
            <div 
              className="category-header" 
              onClick={() => toggleCategory(category.id)}
            >
              <div className="category-title">
                {category.icon}
                <h2>{category.title}</h2>
              </div>
              {activeCategory === category.id ? 
                <ChevronUp size={20} /> : 
                <ChevronDown size={20} />
              }
            </div>
            
            {(activeCategory === category.id || searchTerm) && (
              <div className="articles-list">
                {category.articles.map((article, index) => (
                  <div key={index} className="article-item">
                    <h3>{article.question}</h3>
                    <p>{article.answer}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="help-footer">
        <p>Still need help? <Link to="/contact">Contact our support team</Link> and we'll get back to you as soon as possible.</p>
      </div>
    </div>
  );
};

export default HelpCenter;
